import { useMemo, useState } from "react";

import {
  AccountBalanceOutlined,
  AccountCircleOutlined,
  CorporateFareOutlined,
  HomeOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { isEmpty, isNil } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";

import { grey } from "@mui/material/colors";
import FlxGrid from "../../_src_shared/components/layout/FlxGrid";

import FileUploadFieldWithModal from "../../components/common/FileUploadFieldWithModal";
import SearchInput from "../../components/inputs/SearchInput";
import Screen from "../../components/layout/Screen";
import ScreenContent from "../../components/layout/ScreenContent";
import TitledHeader from "../../components/layout/TitledHeader";
import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import BasicFilesTable from "../../components/tables/BasicFilesTable";
import Htag from "../../components/typography/Htag";

import { useEndpointHook } from "../../hooks/useEndpointHook";
import {
  fileDocGroupAndTypes,
  fileDocGroups,
} from "../../constants/fileDocGroupAndTypes";

const FileUploadScreen = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const documents = useSelector((state) => state?.profile?.borrowerDocuments);

  const [docGroup, setDocGroup] = useState("All Files");
  const onDocumentGroupChange = (d) => {
    console.log("d", d);

    if (isNil(d)) {
      setDocGroup("All Files");
      return;
    }
    setDocGroup(d);
    return;
  };
  // const { loading, getBorrowerDocuments } = useBorrowerPortalHook();
  const [filter, setFilter] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  // useEffect(() => {
  //   console.log("profile", profile);
  //   if (!isArray(documents)) {
  //     getBorrowerDocuments({
  //       onSuccessFn: (data) => {
  //         dispatch(profileUpdate({ borrowerDocuments: data }));
  //         // Handle successful document retrieval
  //       },
  //     });
  //   }
  // }, [profile]);

  // Don't render form until profile data is loaded
  if (!profile) {
    return (
      <Screen>
        <TitledHeader title={"Files"} />
        <ScreenContent>
          <Txt>Loading profile...</Txt>
        </ScreenContent>
      </Screen>
    );
  }

  return (
    <Screen background="#ffffff">
      <TitledHeader title={"Document Library"} />
      <ScreenContent
        sx={{
          p: 0,

          height: "100%",
          width: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          // flexDirection: isMobile ? "column" : "row", // Stack vertically on mobile
          position: "relative",
        }}
      >
        {/* Tab Switcher */}
        <TabSwitcherSidebar
          isMobile
          docGroup={docGroup}
          onChange={onDocumentGroupChange}
        />

        <PrimaryContentArea isMobile={isMobile}>
          <Flx column gap={0} fw>
            <Flx column gap={1} fw>
              {/* <Htag variant="h1">Document Library</Htag> */}
              <SearchInput
                placeholder="Search in files..."
                onChange={setFilter}
                value={filter}
                rounded
                fullWidth
              />
            </Flx>
            <FileDocGroupSection
              quickFilterText={filter}
              icon={<AccountCircleOutlined className="thin" />}
              hideDivider
              title="Track Record & Proof of Experience Documents"
              activeDocGroup={docGroup}
              docGroup="Borrower Documents"
              description="Provide documentation of Track Record such as an itemized transaction history of with past flips including purchase and sale dates and dollar amounts. Do not provide details on past primary residences, only focus on investment properties. If those past transactions were through different entities, you may be asked for proof of entity ownership or purchase and/or sale closing documents."
            />

            <FileDocGroupSection
              quickFilterText={filter}
              icon={<CorporateFareOutlined className="thin" />}
              title="Entity Documents"
              activeDocGroup={docGroup}
              docGroup="Entity Docs"
              description="Upload documents for your LLCs. This includes the LLC you may plan to use for a transaction, an entity you use for you bank accounts or LLCs you have used for projects in the past."
            />

            <FileDocGroupSection
              quickFilterText={filter}
              icon={<AccountBalanceOutlined className="thin" />}
              activeDocGroup={docGroup}
              docGroup="Liquidity"
              description="Bank Statements for the last two months to show cash. For purchases you should be able to show enough liquidity to cover down payment, closing costs and 3-6 months of interest payments. You can upload other proof of assets like availability on a credit line or investment accounts to boost your liquidity profile."
            />

            <FileDocGroupSection
              quickFilterText={filter}
              icon={<HomeOutlined className="thin" />}
              activeDocGroup={docGroup}
              docGroup="Property Documents"
              description={
                "Upload property documents such as purchase contracts, leases, and any other relevant documentation for properties you are interested in or have previously worked with."
              }
            />
          </Flx>
        </PrimaryContentArea>
      </ScreenContent>
    </Screen>
  );
};

const FileDocGroupSection = ({
  title,
  docGroup,
  docType,
  description,
  activeDocGroup,
  quickFilterText,
  hideDivider = false,
  icon,
}) => {
  const [show, setShow] = useState(true);
  const content = (
    <>
      {!hideDivider && activeDocGroup === "All Files" && (
        <Divider sx={{ my: 0 }} />
      )}
      <Box
        column
        key={docGroup}
        sx={{
          px: 1,
          py: 4,
          gap: 0.6,
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          ".upload-text-label": {
            fontStyle: "italic",
            color: "rgba(0, 0, 0, 0.8)",

            lineHeight: 1.43,
            fontSize: "12px",
          },
        }}
      >
        <Flx gap={1} sx={{ alignItems: "flex-start" }}>
          <Flx column sx={{ flexGrow: 1 }}>
            <Flx ac gap={0.4} sx={{ ".MuiSvgIcon-root": { fontSize: "16px" } }}>
              {icon}
              <Txt sx={{ fontSize: "16px", fontWeight: 600 }}>
                {title || docGroup}
              </Txt>
            </Flx>
            <Txt secondary sx={{ fontSize: "13px", color: "text.secondary" }}>
              {description}
            </Txt>
          </Flx>
          <IconButton onClick={() => setShow((s) => !s)}>
            {show ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Flx>
        {show ? (
          <DocTypeCardsRenderer
            quickFilterText={quickFilterText}
            docGroup={docGroup}
            docType={docType}
            label={title}
          />
        ) : null}
      </Box>
    </>
  );
  if (activeDocGroup === "All Files" || activeDocGroup === docGroup) {
    return content;
  }
  return null;
};

const DocTypeCardsRenderer = ({ docGroup, quickFilterText, label }) => {
  const loanDocuments = useSelector(
    (state) => state.loanDrilldown?.loanDocuments,
  );

  const filteredDocGroupDocuments = useMemo(() => {
    if (isEmpty(loanDocuments)) {
      return [];
    }
    return loanDocuments.filter((doc) => {
      return doc?.docGroup === docGroup && !doc?.isHidden;
    });
  }, [loanDocuments, docGroup]);

  const filteredDocTypes = useMemo(() => {
    return fileDocGroupAndTypes[docGroup] || [];
  }, [docGroup]);

  return (
    <FlxGrid width="320px" gap={3} fw wrap sx={{ mt: 2.5 }}>
      {filteredDocTypes?.map((dt) => {
        return (
          <FileUploadCard
            docGroup={docGroup}
            docType={dt}
            label={label}
            files={filteredDocGroupDocuments}
            quickFilterText={quickFilterText}
          />
        );
      })}
    </FlxGrid>
  );
};

const FileUploadCard = ({
  docGroup,
  docType,
  files,
  quickFilterText,
  label,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState(null);
  const { loading, uploadProfileDocuments } = useEndpointHook();
  const profile = useSelector((state) => state?.profile);

  const filteredFiles = useMemo(() => {
    let documents = profile?.borrowerDocuments || [];
    // console.log("documents", { documents, docGroup, docType });
    const visibleDocs = documents.filter((doc) => !doc.isHidden);
    documents = visibleDocs;
    // Filter by docGroup if provided
    if (docGroup) {
      documents = documents.filter((doc) => doc.docGroup === docGroup);
    }

    // Filter by docType if provided (only applies if docGroup filtering didn't exclude all documents)
    if (docType) {
      documents = documents.filter((doc) => doc.docType === docType);
    }

    return documents;
  }, [profile?.borrowerDocuments, docGroup, docType]);

  const handleUploadFiles = (f) => {
    console.log("SUBMITTT", f);
    // return;
    uploadProfileDocuments({
      files: f,

      onSuccessFn: () => {
        setUploadedFiles(null);
      },
    });
  };
  return (
    <Box>
      <FileUploadFieldWithModal
        suppressUploadButton
        borderSize={1.5}
        docGroup={docGroup}
        docType={docType}
        label={docType}
        // hideUploadIcon
        minHeight={"88px"}
        keyName={`file-uploader-card-${docGroup}-${docType}`}
        onSubmit={handleUploadFiles}
        loading={loading}
        inputSx={{
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderBottom: "none !important",
        }}
        // tempTest
        files={uploadedFiles}
        setFiles={setUploadedFiles}
      />
      <Box
        sx={{
          maxHeight: "200px",
          overflowY: "auto",
          // mt: -0.4,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          borderBottomRightRadius: "8px",
          borderBottomLeftRadius: "8px",
          // mt: -0.5,
          ".ag-row-last": {
            borderBottom: "none !important",
          },
        }}
      >
        <BasicFilesTable
          docGroup={docGroup}
          docType={docType}
          files={filteredFiles}
          emptyContent={
            <Box sx={{ p: 2 }}>
              <Txt secondary>No files added</Txt>
            </Box>
          }
          // rowData={files}
          quickFilterText={quickFilterText}
          // sx={{
          //   maxHeight: "200px",
          //   overflowY: "auto",
          //   mt: -0.4,
          //   border: (theme) => `1px solid ${theme.palette.divider}`,
          //   borderRadius: 0,
          //   borderBottomRightRadius: "8px",
          //   borderBottomLeftRadius: "8px",
          //   // mt: -0.5,
          //   ".ag-row-last": {
          //     borderBottom: "none !important",
          //   },
          // }}
          hideIfEmpty
          showFileOptions
          headerHeight={0}
        />
      </Box>
    </Box>
  );
};

const TabSwitcherSidebar = ({ isMobile, docGroup, onChange }) => {
  const tabs = useMemo(() => ["All Files", ...fileDocGroups], []);
  return (
    <Box
      sx={{
        p: isMobile ? 0.5 : 1,
        // pr: 0,
        borderRight: isMobile ? "none" : `1px solid ${grey[300]}`,
        borderBottom: isMobile ? `1px solid ${grey[300]}` : "none",
        // height: isMobile ? "auto" : "100%",
        // width: isMobile ? "100%" : "235px",
        // backgroundColor: "#f5f5f5",
        backgroundColor: "#f5f8fa",
        // backgroundColor: "#fafafa",
        // backgroundColor: "#fff",
        flexShrink: 0, // Prevent shrinking
      }}
    >
      <ToggleTabSwitcher
        tabs={tabs}
        value={docGroup}
        allowDeselect
        variant={"underline"}
        underlinePosition={isMobile ? "bottom" : "right"}
        onChange={onChange}
        sx={{
          width: "100%",
          // Add horizontal scrolling for mobile if tabs are too long
          ...(isMobile && {
            overflowX: "auto",
            whiteSpace: "nowrap",
          }),
        }}
        orientation={isMobile ? "horizontal" : "vertical"}
      />
    </Box>
  );
};

const PageSection = ({
  title,
  icon,
  children,
  activeDocGroup,
  endContent,
  description,
  sx,
}) => {
  // if (activeDocGroup !== title && activeDocGroup !== "All Information") {
  //   return null;
  // }

  const headerContent = (
    <Flx column sx={{ flexGrow: 1 }} gap={1}>
      <Flx gap={1} ac>
        {icon}
        <Htag variant="h3" className="text-center">
          {title}
        </Htag>
      </Flx>
      {description ? <Txt secondary>{description}</Txt> : null}
    </Flx>
  );
  return (
    <>
      <Flx column fw gap={2} sx={{ py: 4, ...sx }}>
        {endContent ? (
          <Flx wrap gap={2} className={"section-header"}>
            {headerContent}
            {endContent}
          </Flx>
        ) : (
          headerContent
        )}
        {children}
      </Flx>
      {activeDocGroup === "All Information" ? <Divider sx={{ my: 6 }} /> : null}
    </>
  );
};

const PrimaryContentArea = ({ isMobile, children }) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        px: 1.4,
        pt: 5,
        overflowX: "hidden",
        overflowY: "auto",
        height: isMobile ? "calc(100% - 80px)" : "100%", // Adjust height on mobile to account for tab switcher
        minHeight: 0, // Important for proper flex behavior
      }}
    >
      <ScreenContent
        maxWidth={"lg"}
        sx={{
          width: "100%",

          // background: "red",
          pb: 14,
          // pt: 4,
          // px: isMobile ? 0 : 5, // Reduce padding on mobile
          margin: "0px auto",
        }}
      >
        {children}
      </ScreenContent>
    </Box>
  );
};

export default FileUploadScreen;
